import React from 'react';
import { History, ArrowRight, X } from 'lucide-react';

/**
 * BackChartingResumeBanner
 * -------------------------
 * Small contextual banner that appears on the landing screen when the
 * provider has unsigned/incomplete notes from prior days. Single-click
 * shortcut to open the History slide panel and resume back-charting.
 *
 * Hidden when there are no incomplete notes — zero footprint on a clean
 * morning. Dismissable for the rest of the session.
 *
 * Feb 16, 2026 — UX improvement requested by user.
 */
const _SESSION_DISMISS_KEY = 'writeright_resume_banner_dismissed';

const _countUnsignedFromPriorDays = (history) => {
  if (!Array.isArray(history) || history.length === 0) return 0;
  const today = new Date();
  let count = 0;
  for (const entry of history) {
    if (!entry || entry.completed === true) continue;
    if (!entry.clinical_note && !entry.admin_letter) continue; // only notes the provider started
    const ts = entry.timestamp || entry.created_at;
    if (!ts) continue;
    try {
      const t = new Date(ts);
      const isSameDay = t.getFullYear() === today.getFullYear()
        && t.getMonth() === today.getMonth()
        && t.getDate() === today.getDate();
      if (!isSameDay) count += 1;
    } catch { /* ignore parse errors */ }
  }
  return count;
};

const BackChartingResumeBanner = ({ history, onOpenHistory }) => {
  const [dismissed, setDismissed] = React.useState(() => {
    try { return sessionStorage.getItem(_SESSION_DISMISS_KEY) === '1'; }
    catch { return false; }
  });
  const count = React.useMemo(() => _countUnsignedFromPriorDays(history), [history]);

  if (dismissed || count === 0) return null;

  return (
    <div
      className="border-2 border-amber-500 bg-amber-900 px-3 py-2.5 flex items-center gap-2 mb-3"
      data-testid="back-charting-resume-banner"
    >
      <div className="w-7 h-7 bg-amber-700 border-2 border-amber-400 flex items-center justify-center flex-shrink-0">
        <History className="w-3.5 h-3.5 text-amber-100" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-100">
          Resume back-charting
        </p>
        <p className="text-xs text-amber-200/80 truncate">
          {count} unsigned note{count !== 1 ? 's' : ''} from prior days waiting to be signed in Genesis.
        </p>
      </div>
      <button
        type="button"
        onClick={onOpenHistory}
        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold border-2 border-amber-300 inline-flex items-center gap-1 transition-colors flex-shrink-0"
        data-testid="back-charting-resume-open-btn"
      >
        Open History
        <ArrowRight className="w-3 h-3" />
      </button>
      <button
        type="button"
        onClick={() => {
          setDismissed(true);
          try { sessionStorage.setItem(_SESSION_DISMISS_KEY, '1'); } catch { /* ignore */ }
        }}
        className="p-1 text-amber-200/70 hover:text-amber-100 hover:bg-amber-700 transition-colors flex-shrink-0"
        title="Dismiss for this session"
        data-testid="back-charting-resume-dismiss-btn"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

export default BackChartingResumeBanner;
